// Add data types to window.navigator for use in this file.
// See https://www.typescriptlang.org/docs/handbook/triple-slash-directives.html#-reference-types-
// for more info.
// eslint-disable-next-line spaced-comment
/// <reference types="user-agent-data-types" />

import React, { useReducer, type ReactNode } from "react";
import { onSafari, isRecordingSupported, isNullUndefinedOrEmpty } from "../util";
import { useTranslation } from "react-i18next";
import { isFirefox, isEdge, isEdgeChromium, isChrome, isSafari, isChromium,
  isMobile, browserVersion, isWindows, isAndroid, isIPhone13, isIPad13, isMacOs } from "react-device-detect";

const FULL_SUPPORT = 0;
const LIMITED_SUPPORT = 1;
const NO_SUPPORT = 2;
// declare const InstallTrigger: any;

const environment = {
  isFirefox: isFirefox,
  isEdge: isEdge || isEdgeChromium,
  isChromium: isChromium,
  isChrome: isChrome,
  isSafari: isSafari,
  version: -1,
  mobile: isMobile,
  platform: "unknown",
};

function assessEnvironment() {
  let isiPhone : boolean = false;
  let isiPad : boolean = false;
  const uad = navigator.userAgentData;

  if (uad === undefined) {
    const ua = navigator.userAgent;
    isiPhone = /iPhone/i.test(ua);
    isiPad = /iPad/i.test(ua);
  }

  if (isWindows) {
    environment.platform = "Windows";
  } else if (isAndroid) {
    environment.platform = "Android";
  } else if (isIPhone13 || isiPhone) {
    environment.platform = "iPhone";
  } else if (isIPad13 || isiPad) {
    environment.platform = "iPad";
  } else if (isMacOs) {
    environment.platform = "macOS";
  } else if (uad !== undefined && uad.platform === "Chrome OS") {
    environment.platform = "Chrome OS";
  }

  environment.version = isNullUndefinedOrEmpty(browserVersion) ? -1 : parseInt(browserVersion);
}

function assessSupport() {
  let support = NO_SUPPORT;

  if (environment.platform === "Windows") {
    if (environment.isChrome && environment.version >= 90) {
      support = FULL_SUPPORT;
    } else if (environment.isEdge && environment.version >= 90) {
      support = FULL_SUPPORT;
    } else if (environment.isFirefox && environment.version >= 68) {
      support = FULL_SUPPORT;
    }
  } else if (environment.platform === "macOS") {
    if (environment.isChrome && environment.version >= 90) {
      support = FULL_SUPPORT;
    } else if (environment.isEdge && environment.version >= 90) {
      support = FULL_SUPPORT;
    } else if (environment.isFirefox && environment.version >= 68) {
      support = FULL_SUPPORT;
    } else if (environment.isSafari && environment.version >= 14) {
      support = FULL_SUPPORT;
    }
  } else if (environment.platform === "iPhone" || environment.platform === "iPad") {
    if (environment.isChrome && environment.version >= 90) {
      support = LIMITED_SUPPORT;
    } else if (environment.isSafari && environment.version >= 14) {
      support = LIMITED_SUPPORT;
    }
  } else if (environment.platform === "Android") {
    if (environment.isChrome && environment.version >= 90) {
      support = LIMITED_SUPPORT;
    }
  } else if (environment.platform === "Chrome OS") {
    if (environment.isChrome && environment.version >= 90) {
      support = FULL_SUPPORT;
    }
  }

  return support;
}

const toastDetails = {
  success: {
    icon: "fa-circle-check",
    text: "Success: This is a success toast.",
  },
  error: {
    icon: "fa-circle-xmark",
    text: "Error: This is an error toast.",
  },
  warning: {
    icon: "fa-triangle-exclamation",
    text: "Warning: This is a warning toast.",
  },
  info: {
    icon: "fa-circle-info",
    text: "Info: This is an information toast.",
  },
} as const;

type ToastDetailsKeyType = keyof typeof toastDetails;

function showToastDetail(type: ToastDetailsKeyType) {
  return toastDetails[type];
}

export type ToastType = {
  id: number;
  status: "success" | "error" | "warning" | "info";
  icon: string;
  link: string;
  title?: string;
  message: string;
  linkText: string;
};

type AddToastItems = (items: ToastType[]) => void;
type DeleteToastItem = (id: number) => void;

export type ToastContextType = {
  items: ToastType[];
  browser: string;
  browserVersion: string;
  platform: string;
  mobile: boolean;
  addItems: AddToastItems;
  deleteItem: DeleteToastItem;
};

const ToastContext = React.createContext<ToastContextType>({
  items: [],
  browser: "",
  browserVersion: "",
  platform: "",
  mobile: false,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  addItems: (items: ToastType[]) => {},
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  deleteItem: (id: number) => {},
});

const defaultToastContext: ToastContextType = {
  items: [],
  browser: "",
  browserVersion: "",
  platform: "",
  mobile: false,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  addItems: (items: ToastType[]) => {},
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  deleteItem: (id: number) => {},
};

type ToastReducerAction = {
  change: string;
  items: ToastType[];
  id: number;
};

const toastReducer = (state: ToastContextType, action: ToastReducerAction): ToastContextType => {
  let updatedState;

  if (action.change === "ADD") {
    updatedState = state.items.concat(action.items);
    updatedState.forEach(item => (item.icon = showToastDetail(item.status).icon));
  } else {
    updatedState = state.items.filter(item => item.id !== action.id);
  }

  return { ...state, items: updatedState };
};

interface ToastContextProviderProps {
  children: ReactNode;
}

export const ToastContextProvider = (props: ToastContextProviderProps) => {
  let id = 1;
  const warnings: ToastType[] = [];
  const { t } = useTranslation();

  // We allow HTTP connections to localhost, as most browsers also seem to allow
  // video capture in those cases.
  const usingUnsecureConnection =
    window.location.protocol !== "https:" &&
    window.location.hostname !== "localhost" &&
    window.location.hostname !== "127.0.0.1";
  if (usingUnsecureConnection) {
    warnings.push({
      id: id++,
      status: "warning",
      icon: toastDetails["warning"].icon,
      message: t("warning-https"),
      link: "",
      linkText: "",
    });
  }

  // Warning about missing `MediaRecorder` support
  if (!isRecordingSupported()) {
    let message = t("warning-recorder-not-supported");
    if (onSafari()) {
      message = `${message} ${t("warning-recorder-safari-hint")}`;
    }
    warnings.push({
      id: id++,
      status: "error",
      icon: toastDetails["error"].icon,
      message: message,
      link: "",
      linkText: "",
    });
  }

  assessEnvironment();
  const support = assessSupport();

  // Is the browser supported?
  if (support === LIMITED_SUPPORT) {
    warnings.push({
      id: id++,
      status: "warning",
      icon: toastDetails["warning"].icon,
      link: "https://enghouseglobal.atlassian.net/servicedesk/customer/portal/21/article/1200423580",
      message: "limited-browser-support",
      linkText: "here",
    });
  }

  // Is the platform supported?
  if (support === NO_SUPPORT) {
    warnings.push({
      id: id++,
      status: "error",
      icon: toastDetails["error"].icon,
      link: "https://enghouseglobal.atlassian.net/servicedesk/customer/portal/21/article/1200423580",
      message: "unsupported-browser",
      linkText: "here",
    });
  }

  // See if any test messages were passed in through parameters.
  const urlParams = new URLSearchParams(window.location.search);
  const success = urlParams.get("Success");
  const info = urlParams.get("Info");
  const warning = urlParams.get("Warning");
  const error = urlParams.get("Error");

  if (success !== null && success !== "") {
    warnings.push({
      id: id++,
      status: "success",
      icon: toastDetails["success"].icon,
      message: success,
      link: "",
      linkText: "",
    });
  }

  if (info !== null && info !== "") {
    warnings.push({
      id: id++,
      status: "info",
      icon: toastDetails["info"].icon,
      message: info,
      link: "",
      linkText: "",
    });
  }

  if (warning !== null && warning !== "") {
    warnings.push({
      id: id++,
      status: "warning",
      icon: toastDetails["warning"].icon,
      message: warning,
      link: "",
      linkText: "",
    });
  }

  if (error !== null && error !== "") {
    warnings.push({
      id: id++,
      status: "error",
      icon: toastDetails["error"].icon,
      message: error,
      link: "",
      linkText: "",
    });
  }

  defaultToastContext.items = warnings;

  const [toastState, dispatchToastAction] = useReducer(toastReducer, defaultToastContext);

  const addItems = (items: ToastType[]) => {
    dispatchToastAction({ change: "ADD", items: items, id: -1 });
  };

  const deleteItem = (id: number) => {
    dispatchToastAction({ change: "REMOVE", id: id, items: [] });
  };

  let brandsText = "";

  if (environment.isEdge) {
    brandsText = "Microsoft Edge";
  } else if (environment.isFirefox) {
    brandsText = "Mozilla Firefox";
  } else if (environment.isChrome) {
    brandsText = "Google Chrome";
  } else if (environment.isSafari) {
    brandsText = "Apple Safari";
  }

  if (isNullUndefinedOrEmpty(brandsText) && navigator.userAgentData !== undefined) {
    brandsText = "(";
    navigator.userAgentData.brands.forEach(item => {
      brandsText = brandsText === "(" ? item.brand : `${brandsText}, ${item.brand}`;
    });

    brandsText = `${brandsText})`;
  } else if (isNullUndefinedOrEmpty(brandsText)) {
    brandsText = navigator.userAgent;
  }

  const toastContext: ToastContextType = {
    items: toastState.items,
    browser: brandsText,
    browserVersion: environment.version.toString(),
    platform: environment.platform,
    mobile: environment.mobile,
    addItems: addItems,
    deleteItem: deleteItem,
  };

  return (
    <ToastContext.Provider value={toastContext}>
      {props.children}
    </ToastContext.Provider>
  );
};

export default ToastContext;
